/**
 * API Client Configuration
 * Axios instance with auth headers and error handling
 */

import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import { getTenantFromPath } from './tenant';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

// Request interceptor to add auth token and tenant context
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add tenant context from path (NEW - path-based routing)
    const pathTenant = getTenantFromPath();
    if (pathTenant) {
      // Send the tenant subdomain in the path header
      config.headers['X-Tenant-Path'] = window.location.pathname;
      config.headers['X-Tenant-Subdomain'] = pathTenant;
    }
    
    // Add tenant context from user if available (fallback)
    const userTenant = useAuthStore.getState().user?.tenantId;
    if (userTenant && !pathTenant) {
      config.headers['X-Tenant-ID'] = userTenant;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;
    
    if (response?.status === 401) {
      // Token expired or invalid
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    
    // Format error message
    const message = response?.data?.error || 
                   response?.data?.message || 
                   error.message || 
                   'An error occurred';
    
    return Promise.reject({
      message,
      status: response?.status,
      code: response?.data?.code,
      details: response?.data?.details
    });
  }
);

export default api;
